import { supabase } from '@/integrations/supabase/client';

export interface SessionBookingWithMentor {
  id: string;
  mentor_id: string;
  mentee_id: string;
  application_id?: string;
  booked_date: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  notes?: string;
  created_at: string;
  updated_at: string;
  mentor: {
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
  is_subscription_active?: boolean;
  subscription_end_date?: string;
  course_type?: string;
  subscription_type?: string;
}

export const getMenteeBookings = async (menteeId: string) => {
  try {
    const { data, error } = await supabase
      .from('session_bookings')
      .select(`
        *,
        applications!session_bookings_application_id_fkey (
          application_id,
          is_subscription_active,
          subscription_end_date,
          courses!applications_course_id_fkey (
            course_type,
            subscription_type
          )
        )
      `)
      .eq('mentee_id', menteeId)
      .order('booked_date', { ascending: true });

    if (error) throw error;
    
    if (!data || data.length === 0) {
      return { success: true, data: [] };
    }

    // Get unique mentor IDs and fetch their profiles
    const uniqueMentorIds = [...new Set(data.map(booking => booking.mentor_id))];
    const { data: mentorProfiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, avatar_url')
      .in('id', uniqueMentorIds);

    if (profilesError) throw profilesError;
    
    // Type cast and flatten the data to match our interface
    const typedData = data?.map(booking => {
      const mentorProfile = mentorProfiles?.find(p => p.id === booking.mentor_id);
      return {
        ...booking,
        status: booking.status as 'pending' | 'confirmed' | 'cancelled' | 'completed',
        application_id: booking.applications?.application_id,
        mentor: mentorProfile ? {
          first_name: mentorProfile.first_name,
          last_name: mentorProfile.last_name,
          avatar_url: mentorProfile.avatar_url
        } : {
          first_name: '',
          last_name: '',
          avatar_url: ''
        },
        is_subscription_active: booking.applications?.is_subscription_active,
        subscription_end_date: booking.applications?.subscription_end_date,
        course_type: booking.applications?.courses?.course_type,
        subscription_type: booking.applications?.courses?.subscription_type
      };
    }) as SessionBookingWithMentor[];
    
    return { success: true, data: typedData };
  } catch (error) {
    console.error('Error fetching mentee bookings:', error);
    return { success: false, error };
  }
};